import React, { useContext, useCallback } from "react";
import { Box, Stack, useBreakpointValue, useColorModeValue, useDisclosure, useToast } from "@chakra-ui/react";
import type { Answer, Question } from "types/question";
import { AnswerStatus } from "types/question";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import { ContentFilterContext } from "screens/content";
import { SmallActionVerificationBadge } from "screens/content/contentCanvas/cell/SmallActionVerificationBadge";
import { MarkupTextViewer } from "screens/content/contentView/sections/MarkupTextViewer";
import { BiCopy } from "react-icons/bi";
import { SmallActionButton } from "screens/content/contentCanvas/cell/SmallActionButton";
import { ProjectThumbHotlinks } from "./ProjectActions/ProjectThumbHotlinks";
import { useCollectionKey } from "hooks";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import { AiOutlineDelete } from "react-icons/ai";
import { VerifyDeleteModal } from "screens/content/contentView/VerifyDeleteModal";
import { sendMessage } from "state/websocket/operations";
import { useDispatch } from "react-redux";
import type { RequestEntities } from "types/charliui";
import { ToastMessageContent } from "screens/common/components";
import { ConversationContext } from "screens/thread/ConversationContext";
import { useCopyValue } from "hooks/useCopies";
import type { HotlinkAction } from "types/copy";

interface AnswerCardProps {
  answerRecord?: Answer;
  questionRecord?: Question;
  collectionId: string;
  conversationId: string;
  enableHelpPopover?: boolean;
}

const copyAnswer = (question?: Question) => {
  if (!question) return "";
  let answerText = "";
  if (question.question) {
    answerText += `Question: ${question.question}\r\n`;
  }
  if (question.answers && Array.isArray(question.answers)) {
    question.answers.forEach((answer) => {
      if (answer.answer) {
        answerText += `${answer.answer}\r\n`;
      }
    });
  }
  return answerText + "\r\n";
};

const noAnswerCopy =
  "Sorry but I couldn't find an answer to your question using the resources available in this project. Can you try asking that question another way?";

export const AnswerCardPinned: React.FC<React.PropsWithChildren<React.PropsWithChildren<AnswerCardProps>>> = ({
  answerRecord,
  questionRecord,
  collectionId,
  conversationId,
  enableHelpPopover,
}) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const { onVerifiedAIPanelOpen } = useAddToCharliContext();
  const { setSelectedResult, searchText } = useContext(ContentFilterContext);
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const shareDetails = useCollectionKey(collectionId, "shareDetails");
  const isSharedRead = shareDetails && shareDetails.accessMode === "read";
  const dispatch = useDispatch();
  const toast = useToast();
  const { setConversationId } = useContext(ConversationContext);
  const { isOpen: isOpenConfirmDelete, onOpen: onOpenConfirmDelete, onClose: onCloseConfirmDelete } = useDisclosure();

  const thumbsUpHotlinkActionsRaw = useCopyValue("copy_thumbs_up_hotlink_actions_answers");
  const thumbsDownHotlinkActionsRaw = useCopyValue("copy_thumbs_down_hotlink_actions_answers");

  const thumbsUpHotlinkActions: HotlinkAction[] = thumbsUpHotlinkActionsRaw.map((item: string) => JSON.parse(item));
  const thumbsDownHotlinkActions: HotlinkAction[] = thumbsDownHotlinkActionsRaw.map((item: string) => JSON.parse(item));

  const updateAnswerStatus = useCallback(
    (reason?: string) => {
      if (!conversationId) {
        return;
      }
      const entities: RequestEntities = [];
      answerRecord?.id &&
        entities.push({
          entity: `${questionRecord ? "question_id" : "answer_id"}`,
          value: questionRecord ? questionRecord.id : answerRecord.id,
        });
      entities.push({ entity: `${questionRecord ? "question_status" : "answer_status"}`, value: AnswerStatus.unpinned });
      entities.push({ entity: "content_delete_reason", value: reason || "other" });
      collectionId && entities.push({ entity: "collection_id", value: collectionId });

      dispatch(
        sendMessage({
          conversationId: conversationId,
          intent: `${questionRecord ? "/manage_question" : "/manage_answer"}`,
          entities: entities,
        })
      );
      toast({
        render: ({ onClose }) => (
          <ToastMessageContent
            message={`I'll remove this result from your project now. You can open the conversation I started about it by clicking this message`}
            onClick={() => {
              conversationId && setConversationId(conversationId);
              onClose();
            }}
            onClose={onClose}
          />
        ),
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
    [conversationId, answerRecord?.id, questionRecord, collectionId, dispatch, toast, setConversationId]
  );

  const handleOnClick = useCallback(() => {
    setSelectedResult({
      questionText: questionRecord?.question || "",
      resultId: answerRecord?.id || questionRecord?.id || "",
      resultStatus: answerRecord?.answerStatus || questionRecord?.status || AnswerStatus.pinned,
      resultText: answerRecord?.answer || noAnswerCopy || "",
      confidence: answerRecord?.confidence || 0,
      attributionMetadataIds: answerRecord?.metadataIds || [],
      collectionId: collectionId,
      conversationId: conversationId,
      isQuestion: answerRecord?.answer && answerRecord?.answer.length > 0 ? false : true,
      verifiedStatus: answerRecord?.answer && answerRecord?.answer.length > 0 ? "verified" : "unverified",
      resultType: "answer",
      focus: questionRecord?.focus === "analytical" ? "fundamentals" : questionRecord?.focus || "",
    });
  }, [answerRecord, questionRecord, collectionId, conversationId, setSelectedResult]);

  const renderSmallActionVerificationBadge = useCallback(() => {
    return (
      <SmallActionVerificationBadge
        className={`ch-qa-result-badge-${answerRecord?.answer && answerRecord?.answer.length > 0 ? "verified" : "unverified"}`}
        shieldColor={answerRecord?.answer && answerRecord?.answer.length > 0 ? "blue" : "gray"}
        shieldTooltip={
          answerRecord?.answer && answerRecord?.answer.length > 0
            ? "Verified by AI. Click to see the details."
            : "No answer available. Click to see the details."
        }
        onClick={() => {
          handleOnClick();
          onVerifiedAIPanelOpen();
        }}
      />
    );
  }, [answerRecord, handleOnClick, onVerifiedAIPanelOpen]);

  if (!answerRecord && !questionRecord) {
    return null;
  } else {
    return (
      <Stack>
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent={"flex-start"}
          spacing="1rem"
          width="100%"
          px={isMobile ? ".5rem" : "1rem"}
          pt={isMobile ? ".5rem" : "1rem"}>
          {!isMobile && renderSmallActionVerificationBadge()}
          <ShowMoreContainer height="15rem" minHeight="7rem">
            <MarkupTextViewer
              className="ch-qa-result-answer"
              markupText={answerRecord?.answer && answerRecord?.answer.length > 0 ? answerRecord?.answer : noAnswerCopy}
              searchText={searchText}
            />
          </ShowMoreContainer>
        </Stack>
        <Stack
          spacing={isMobile ? ".5rem" : "1rem"}
          width="100%"
          height="2rem"
          px={isMobile ? ".5rem" : "1rem"}
          py={isMobile ? ".2rem" : ".5rem"}
          direction="row"
          borderTop="1px solid"
          borderColor={borderColor}
          align="center">
          <Stack spacing={isMobile ? ".5rem" : "1rem"} width="100%" direction="row" align="center">
            {isMobile && renderSmallActionVerificationBadge()}
            <ProjectThumbHotlinks
              isDisabled={isSharedRead}
              action="up"
              recordId={answerRecord?.id}
              feedbackIntent="submit_answer_feedback"
              collectionId={collectionId}
              hotlinkActions={thumbsUpHotlinkActions}
            />
            <ProjectThumbHotlinks
              isDisabled={isSharedRead}
              action="down"
              recordId={answerRecord?.id}
              collectionId={collectionId}
              feedbackIntent="submit_answer_feedback"
              hotlinkActions={thumbsDownHotlinkActions}
            />
            <SmallActionButton
              iconSize="1rem"
              isDisabled={isSharedRead}
              classNames="ch-copy-record"
              iconTypeName={BiCopy}
              onClick={() => navigator.clipboard.writeText(copyAnswer(questionRecord))}
              tooltip={"Copy Answer"}
            />
          </Stack>
          <Box>
            <SmallActionButton
              isDisabled={isSharedRead}
              iconTypeName={AiOutlineDelete}
              onClick={onOpenConfirmDelete}
              tooltip={`Remove Result`}
            />
            <VerifyDeleteModal
              title="Confirm Remove Result"
              message="Please confirm that you would like to remove this result and select one of the reasons below. The reason will be used in future AI learning to improve results."
              onHandleConfirmDelete={(reason: string) => updateAnswerStatus(reason)}
              onClose={onCloseConfirmDelete}
              isOpen={isOpenConfirmDelete}
            />
          </Box>
        </Stack>
      </Stack>
    );
  }
};
